"use client";

import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare, CheckCircle, XCircle, Clock, AlertTriangle,
  RefreshCw, Save, Loader2
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { useTabbedChrome } from '@/components/weavein';

// ── Types ───────────────────────────────────────────────────────────

interface WABAStatus {
  organization_id: number;
  id?: string;
  waba_id: string | null;
  business_name: string | null;
  verification_status: string;
  messaging_tier: string | null;
  daily_send_limit: number | null;
  display_phone_number: string | null;
  phone_number_id: string | null;
  quality_rating: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// ── Helpers ─────────────────────────────────────────────────────────

const tierLabels: Record<string, string> = {
  tier_1: 'Tier 1 (1,000/day)',
  tier_2: 'Tier 2 (10,000/day)',
  tier_3: 'Tier 3 (100,000/day)',
  tier_4: 'Tier 4 (Unlimited)',
};

const qualityColors: Record<string, string> = {
  green: 'text-green-700 bg-green-100',
  yellow: 'text-yellow-700 bg-yellow-100',
  red: 'text-red-700 bg-red-100',
};

const verificationIcons: Record<string, React.ElementType> = {
  verified: CheckCircle,
  pending: Clock,
  rejected: XCircle,
  not_started: AlertTriangle,
  not_registered: AlertTriangle,
};

// ── Component ───────────────────────────────────────────────────────

export const WhatsAppBusinessSettings: React.FC = () => {
  const tabbed = useTabbedChrome();
  const organizationId = localStorage.getItem('active_cbo_id') ? Number(localStorage.getItem('active_cbo_id')) : null;
  const [status, setStatus] = useState<WABAStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    waba_id: '',
    business_name: '',
    display_phone_number: '',
    phone_number_id: '',
  });

  const headers: Record<string, string> = (() => {
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
      ...(cboId && { 'X-CBO-ID': cboId }),
    };
  })();

  const fetchStatus = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${getWeaveinApiUrl()}/api/whatsapp-business/${organizationId}/status`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        setFormData({
          waba_id: data.waba_id || '',
          business_name: data.business_name || '',
          display_phone_number: data.display_phone_number || '',
          phone_number_id: data.phone_number_id || '',
        });
      }
    } catch (err) {
      console.error('Failed to fetch WABA status:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const saveSettings = async () => {
    if (!organizationId) return;
    setSaving(true);
    try {
      const endpoint = status?.id
        ? `${getWeaveinApiUrl()}/api/whatsapp-business/${organizationId}/update`
        : `${getWeaveinApiUrl()}/api/whatsapp-business/register`;
      const method = status?.id ? 'PUT' : 'POST';

      const body = status?.id
        ? formData
        : { ...formData, organization_id: organizationId };

      await fetch(endpoint, { method, headers, body: JSON.stringify(body) });
      fetchStatus();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading WhatsApp Business settings...
      </div>
    );
  }

  const verificationStatus = status?.verification_status || 'not_registered';
  const VerifIcon = verificationIcons[verificationStatus] || AlertTriangle;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        {!tabbed && (
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-green-600" />
          WhatsApp Business Account
        </h2>
        )}
        <p className={`text-sm text-gray-500 ${tabbed ? '' : 'mt-1'}`}>
          Configure your WhatsApp Business Account (WABA) for outbound messaging
        </p>
      </div>

      {/* Status Card */}
      <div className="bg-white border rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase">Registration Status</h3>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
            verificationStatus === 'verified' ? 'bg-green-100 text-green-700' :
            verificationStatus === 'pending' ? 'bg-yellow-100 text-yellow-700' :
            'bg-gray-100 text-gray-600'
          }`}>
            <VerifIcon className="w-3.5 h-3.5" />
            {verificationStatus.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </span>
        </div>

        {status?.messaging_tier && (
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Messaging Tier</p>
              <p className="text-sm font-medium text-gray-900">{tierLabels[status.messaging_tier] || status.messaging_tier}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Daily Send Limit</p>
              <p className="text-sm font-medium text-gray-900">{status.daily_send_limit?.toLocaleString() || 'N/A'}</p>
            </div>
            {status.quality_rating && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Quality Rating</p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${qualityColors[status.quality_rating] || 'text-gray-600 bg-gray-100'}`}>
                  {status.quality_rating.charAt(0).toUpperCase() + status.quality_rating.slice(1)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Configuration Form */}
        <div className="space-y-4 mt-6 border-t pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WABA ID</label>
              <input
                type="text"
                value={formData.waba_id}
                onChange={(e) => setFormData({ ...formData, waba_id: e.target.value })}
                placeholder="Meta WhatsApp Business Account ID"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
              <input
                type="text"
                value={formData.business_name}
                onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                placeholder="Your organization name"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Phone Number</label>
              <input
                type="text"
                value={formData.display_phone_number}
                onChange={(e) => setFormData({ ...formData, display_phone_number: e.target.value })}
                placeholder="+1 (555) 000-0000"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number ID</label>
              <input
                type="text"
                value={formData.phone_number_id}
                onChange={(e) => setFormData({ ...formData, phone_number_id: e.target.value })}
                placeholder="Twilio WhatsApp Sender SID"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={saveSettings}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {status?.id ? 'Update' : 'Register'} WABA
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppBusinessSettings;
